import { motion, type Variants } from 'framer-motion'

import type { IAnimatedOptimoIconProps } from './AnimatedOptimoIcon.types'
import * as S from './AnimatedOptimoIcon.styles'

export function AnimatedOptimoIcon({ size = 52, className }: IAnimatedOptimoIconProps) {
    // Container animation for overall coordination
    const containerVariants: Variants = {
        animate: {
            transition: {
                staggerChildren: 0.2,
                repeat: Infinity,
                repeatType: 'loop' as const,
                duration: 3,
            },
        },
    }

    // Bottom circle (body) - foundation that drives the bounce
    const bottomCircleVariants: Variants = {
        animate: {
            y: [0, 1, -3, 0], // Slight compression, then bounce
            scaleY: [1, 0.98, 1.02, 1], // Subtle squash and stretch
            transition: {
                duration: 2.4,
                repeat: Infinity,
                ease: [0.68, -0.55, 0.265, 1.55], // Custom bouncy easing
                delay: 0,
            },
        },
    }

    // Middle circle (torso) - launched by base, drives head
    const middleCircleVariants: Variants = {
        animate: {
            y: [0, 0, -8, 0], // Slight delay before bounce, then medium bounce
            x: [0, 0, 1, 0], // Minimal horizontal wobble
            scaleY: [1, 1, 1.03, 1], // Slight stretch at peak
            transition: {
                duration: 2.4,
                repeat: Infinity,
                ease: [0.68, -0.55, 0.265, 1.55],
                delay: 0.1, // Starts bouncing after base begins
            },
        },
    }

    // Top circle (head) - maximum bounce, most dramatic effect
    const topCircleVariants: Variants = {
        animate: {
            y: [0, 0, -14, 0], // Delay, then highest bounce
            x: [0, 0, 2, 0], // More horizontal wobble
            rotate: [0, 0, 8, -4, 0], // Head wobble at peak
            scaleY: [1, 1, 1.05, 1], // More stretch at peak
            transition: {
                duration: 2.4,
                repeat: Infinity,
                ease: [0.68, -0.55, 0.265, 1.55],
                delay: 0.2, // Starts bouncing after middle begins
            },
        },
    }

    // Letter animations - bounce response like sitting on the bouncing logo
    const letterVariants = (delay: number): Variants => ({
        animate: {
            y: [0, 1, -2, 0], // Slight compression then bounce - mirrors the logo physics
            scaleY: [1, 0.98, 1.01, 1], // Subtle squash and stretch
            transition: {
                duration: 2.4, // Same duration as circles for sync
                repeat: Infinity,
                ease: [0.68, -0.55, 0.265, 1.55], // Same bouncy easing as circles
                delay: 0.3 + delay * 0.05, // All letters start after logo bounce begins, with subtle stagger
            },
        },
    })

    // Calculate proportional dimensions based on original SVG (344x76)
    const aspectRatio = 76 / 344
    const width = size * 3.3 // Scale to show full logo
    const height = width * aspectRatio

    return (
        <S.Container className={className}>
            <motion.svg
                width={width}
                height={height}
                viewBox="0 0 344 76"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                variants={containerVariants}
                animate="animate"
                overflow="visible"
            >
                {/* Letter animations - bounce response synchronized with logo */}
                <motion.path
                    d="M316.226 58.4048C309.992 58.4048 305.006 56.4982 301.266 52.6848C297.599 48.7982 295.766 43.6648 295.766 37.2848C295.766 30.9048 297.599 25.7715 301.266 21.8848C305.006 17.9248 309.956 15.9448 316.116 15.9448C322.349 15.9448 327.336 17.9248 331.076 21.8848C334.816 25.7715 336.686 30.8682 336.686 37.1748C336.686 43.5548 334.816 48.6882 331.076 52.5748C327.409 56.4615 322.459 58.4048 316.226 58.4048ZM316.116 50.5948C319.562 50.5948 322.422 49.4948 324.696 47.2948C327.042 45.0948 328.216 41.7582 328.216 37.2848C328.216 32.9582 327.042 29.6215 324.696 27.2748C322.422 24.9282 319.562 23.7548 316.116 23.7548C312.596 23.7548 309.736 24.9282 307.536 27.2748C305.336 29.6215 304.236 32.9582 304.236 37.2848C304.236 41.6115 305.336 44.9115 307.536 47.1848C309.736 49.4582 312.596 50.5948 316.116 50.5948Z"
                    fill="#5B34E9"
                    variants={letterVariants(5)}
                />
                <motion.path
                    d="M231.377 22.3252C232.477 20.6385 234.054 19.1718 236.107 17.9252C238.234 16.6785 240.764 16.0552 243.697 16.0552C246.924 16.0552 249.71 16.7152 252.057 18.0352C254.404 19.3552 256.274 21.1885 257.667 23.5352C258.987 21.6285 260.784 19.9052 263.057 18.3652C265.404 16.8252 268.3 16.0552 271.747 16.0552C276.954 16.0552 280.987 17.6318 283.847 20.7852C286.78 23.9385 288.247 28.2285 288.247 33.6552V56.4252C288.247 57.0852 287.954 57.4152 287.367 57.4152H280.987C280.4 57.4152 280.107 57.0852 280.107 56.4252V35.0852C280.107 31.1985 279.227 28.3385 277.467 26.5052C275.707 24.5985 273.324 23.6452 270.317 23.6452C267.164 23.6452 264.634 24.6352 262.727 26.6152C260.894 28.5218 259.977 31.4918 259.977 35.5252V56.4252C259.977 57.0852 259.684 57.4152 259.097 57.4152H252.607C252.02 57.4152 251.727 57.0852 251.727 56.4252V35.0852C251.727 31.1985 250.847 28.3385 249.087 26.5052C247.4 24.5985 245.054 23.6452 242.047 23.6452C238.82 23.6452 236.254 24.6352 234.347 26.6152C232.514 28.5218 231.597 31.4918 231.597 35.5252V56.4252C231.597 57.0852 231.34 57.4152 230.827 57.4152H224.337C223.75 57.4152 223.457 57.0852 223.457 56.4252V17.8152C223.457 17.2285 223.75 16.9352 224.337 16.9352H224.887C228.554 16.9352 230.717 18.7318 231.377 22.3252Z"
                    fill="#5B34E9"
                    variants={letterVariants(4)}
                />
                <motion.path
                    d="M205.121 6.04486C205.121 4.57819 205.598 3.3682 206.551 2.41486C207.504 1.46153 208.678 0.984863 210.071 0.984863C211.391 0.984863 212.528 1.46153 213.481 2.41486C214.434 3.3682 214.911 4.57819 214.911 6.04486C214.911 7.43819 214.434 8.61153 213.481 9.56486C212.528 10.5182 211.391 10.9949 210.071 10.9949C208.678 10.9949 207.504 10.5182 206.551 9.56486C205.598 8.61153 205.121 7.43819 205.121 6.04486ZM206.771 57.4149C206.184 57.4149 205.891 57.0849 205.891 56.4249V17.9249C205.891 17.2649 206.184 16.9349 206.771 16.9349H207.321C209.448 16.9349 211.098 17.5582 212.271 18.8049C213.518 20.0515 214.141 21.9582 214.141 24.5249V56.4249C214.141 57.0849 213.848 57.4149 213.261 57.4149H206.771Z"
                    fill="#5B34E9"
                    variants={letterVariants(3)}
                />
                <motion.path
                    d="M181.981 24.1952H174.501C173.914 24.1952 173.621 23.9018 173.621 23.3152V17.8152C173.621 17.2285 173.914 16.9352 174.501 16.9352H181.981V3.29518C181.981 2.63518 182.238 2.30518 182.751 2.30518H183.301C185.428 2.30518 187.078 2.96518 188.251 4.28518C189.498 5.53185 190.121 7.43851 190.121 10.0052V16.9352H198.591C199.104 16.9352 199.361 17.2285 199.361 17.8152V23.3152C199.361 23.9018 199.104 24.1952 198.591 24.1952H190.121V56.4252C190.121 57.0852 189.828 57.4152 189.241 57.4152H182.751C182.238 57.4152 181.981 57.0852 181.981 56.4252V24.1952Z"
                    fill="#5B34E9"
                    variants={letterVariants(2)}
                />
                <motion.path
                    d="M137.972 52.0248V74.0248C137.972 74.6848 137.715 75.0148 137.202 75.0148H130.712C130.125 75.0148 129.832 74.6848 129.832 74.0248V17.8148C129.832 17.2282 130.125 16.9348 130.712 16.9348H131.262C133.242 16.9348 134.745 17.5215 135.772 18.6948C136.799 19.7948 137.459 21.1148 137.752 22.6548C138.999 20.7482 140.759 19.1715 143.032 17.9248C145.305 16.6048 148.019 15.9448 151.172 15.9448C157.259 15.9448 161.989 17.9615 165.362 21.9948C168.735 25.9548 170.422 31.0148 170.422 37.1748C170.422 41.0615 169.689 44.6182 168.222 47.8448C166.755 51.0715 164.629 53.6382 161.842 55.5448C159.055 57.4515 155.682 58.4048 151.722 58.4048C148.862 58.4048 146.222 57.8915 143.802 56.8648C141.382 55.8382 139.439 54.2248 137.972 52.0248ZM150.182 50.7048C151.942 50.7048 153.739 50.2648 155.572 49.3848C157.405 48.5048 158.909 47.0748 160.082 45.0948C161.329 43.1148 161.952 40.4748 161.952 37.1748C161.952 32.6282 160.742 29.2548 158.322 27.0548C155.975 24.8548 153.189 23.7548 149.962 23.7548C148.275 23.7548 146.479 24.1215 144.572 24.8548C142.739 25.5882 141.162 26.9448 139.842 28.9248C138.595 30.8315 137.972 33.5815 137.972 37.1748C137.972 40.6948 138.632 43.4448 139.952 45.4248C141.272 47.4048 142.885 48.7982 144.792 49.6048C146.699 50.3382 148.495 50.7048 150.182 50.7048Z"
                    fill="#5B34E9"
                    variants={letterVariants(1)}
                />
                <motion.path
                    d="M101.532 58.4048C95.2989 58.4048 90.3123 56.4982 86.5723 52.6848C82.9056 48.7982 81.0723 43.6648 81.0723 37.2848C81.0723 30.9048 82.9056 25.7715 86.5723 21.8848C90.3123 17.9248 95.2623 15.9448 101.422 15.9448C107.656 15.9448 112.642 17.9248 116.382 21.8848C120.122 25.7715 121.992 30.8682 121.992 37.1748C121.992 43.5548 120.122 48.6882 116.382 52.5748C112.716 56.4615 107.766 58.4048 101.532 58.4048ZM101.422 50.5948C104.869 50.5948 107.729 49.4948 110.002 47.2948C112.349 45.0948 113.522 41.7582 113.522 37.2848C113.522 32.9582 112.349 29.6215 110.002 27.2748C107.729 24.9282 104.869 23.7548 101.422 23.7548C97.9023 23.7548 95.0423 24.9282 92.8423 27.2748C90.6423 29.6215 89.5423 32.9582 89.5423 37.2848C89.5423 41.6115 90.6423 44.9115 92.8423 47.1848C95.0423 49.4582 97.9023 50.5948 101.422 50.5948Z"
                    fill="#5B34E9"
                    variants={letterVariants(0)}
                />

                {/* Circle animations - snowman bounce effect */}
                <motion.path
                    d="M63.5 56.2141C63.5 62.881 48.897 67.357 31.5 67.357C14.103 67.357 0 62.881 0 56.2141C0 48.5 14.103 42.2856 31.5 42.2856C48.897 42.2856 63.5 48.5 63.5 56.2141Z"
                    fill="#5B34E9"
                    variants={bottomCircleVariants}
                    style={{ transformOrigin: '31.5px 54.8px' }}
                />
                <motion.path
                    d="M61 33.25C61 38.3784 48.0163 43.5 32 43.5C15.9837 43.5 3 38.3784 3 33.25C3 27.4465 15.9837 23.9644 32 23.9644C48.0163 23.9644 61 27.4465 61 33.25Z"
                    fill="#5B34E9"
                    variants={middleCircleVariants}
                    style={{ transformOrigin: '32px 33.7px' }}
                />
                <motion.path
                    d="M47 12C47 17.1071 40.0604 24.2142 31.5 24.2142C22.9396 24.2142 16 17.1071 16 12C16 4.57147 22.9396 0 31.5 0C40.0604 0 47 4.57147 47 12Z"
                    fill="#5B34E9"
                    variants={topCircleVariants}
                    style={{ transformOrigin: '31.5px 12.1px' }}
                />
            </motion.svg>
        </S.Container>
    )
}
